import { deepMerge } from '../Object'

export class LocalStorageManager<T extends object> {

  readonly scope: string

  constructor(scope: string, initialState: T) {
    this.scope = scope

    const lsData = this.mergeInitialStateWithLSData<T>(initialState)
    localStorage.setItem(this.scope, JSON.stringify(lsData))
  }

  all(): T {
    const stringMap = localStorage.getItem(this.scope)
    return stringMap ? JSON.parse(stringMap) as T : {} as T
  }

  get<K extends keyof T>(key: K): T[K] {
    return this.all()[key]
  }

  remove<K extends keyof T>(key: K): void {
    const map = this.all()

    if (!map) {
      return
    }

    const mapToSave = this.removeKey(key, map)
    const stringMapToSave = JSON.stringify(mapToSave)

    localStorage.setItem(this.scope, stringMapToSave)
  }

  put<K extends keyof T>(key: K, value: T[K]): void {
    const map = this.all()
    const newMap = map && {...(map as object), [key]: value}
    const mapToSave = newMap ? newMap : {}
    const stringMap = JSON.stringify(mapToSave)

    localStorage.setItem(this.scope, stringMap)
  }

  private removeKey<K extends keyof T>(key: K, obj: T) {
    const {[key]: ignored, ...rest} = (obj as object)
    return rest
  }

  private mergeInitialStateWithLSData<T>(initialState: T): T {
    const lsData = localStorage.getItem(this.scope)

    if (!lsData) return initialState

    return deepMerge(initialState, JSON.parse(lsData))
  }
}
